import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  ArrowLeft,
  Settings,
  Truck,
  Mail,
  TestTube2,
  Save,
  Plus,
  Trash2,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Globe,
  Send,
  Clock,
  CreditCard,
  AlertTriangle,
  Users
} from "lucide-react";

const AdminSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("hello@turbo360.com.au");
  const [newPackage, setNewPackage] = useState({ name: '', price: '', description: '', enabled: true });
  const [showAddPackage, setShowAddPackage] = useState(false);
  const [newFamilyPackage, setNewFamilyPackage] = useState({ name: '', price: '', description: '', guest_count: '', enabled: true });
  const [showAddFamilyPackage, setShowAddFamilyPackage] = useState(false);
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [newOverride, setNewOverride] = useState({
    label: '', start_date: '', end_date: '', apply_monday: true, apply_friday: true,
    allow_morning: false, allow_afternoon: false, enabled: true
  });
  
  const [settings, setSettings] = useState({
    shop_name: "Umoona Opal Mine",
    shop_url: "https://umoonaopalmine.com.au",
    currency: "AUD",
    currency_symbol: "$",
    tax_rate: 0.10,
    tax_inclusive: true,
    shipping: {
      shipping_fee: 10.0,
      free_shipping_threshold: null,
      allowed_countries: ["AU"]
    },
    email: {
      smtp_host: "",
      smtp_port: 587,
      smtp_user: "",
      smtp_password: "",
      sender_email: "",
      sender_name: "Umoona Opal Mine",
      admin_notification_emails: [],
      customer_reply_to: null
    },
    opening_hours: {
      closed_on_tuesdays: false,
      holiday_hours_notice: false
    },
    features: {
      tour_booking_enabled: true
    },
    stripe: {
      use_live_keys: false,
      test_api_key: "",
      test_publishable_key: "",
      test_webhook_secret: "",
      live_api_key: "",
      live_publishable_key: "",
      live_webhook_secret: ""
    }
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        headers
      });

      if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      setSettings(data);
      setError("");
    } catch (err) {
      console.error('Settings fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (section = null) => {
    try {
      setSaving(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`;
      let payload = settings;

      if (section === 'shipping') {
        url += '/shipping';
        payload = settings.shipping;
      } else if (section === 'email') {
        url += '/email';
        payload = settings.email;
      } else if (section === 'opening_hours') {
        url += '/opening-hours';
        payload = settings.opening_hours;
      } else if (section === 'features') {
        url += '/features';
        payload = settings.features;
      } else if (section === 'stripe') {
        url += '/stripe';
        payload = settings.stripe;
      } else if (section === 'tour_pricing') {
        url += '/tour-pricing';
        // Include both tour_pricing and tour_schedule when saving tours settings
        payload = {
          tour_pricing: settings.tour_pricing,
          tour_schedule: settings.tour_schedule || { restrict_monday_friday: true }
        };
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      setSuccess(`${section ? section.charAt(0).toUpperCase() + section.slice(1) : 'Settings'} updated successfully!`);
      setError("");
      
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const testEmailSettings = async () => {
    try {
      setTestingEmail(true);
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email/test?test_email=${encodeURIComponent(testEmail)}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setSuccess(`Test email sent successfully to ${testEmail}!`);
      setError("");
      
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const testOrderConfirmationEmail = async () => {
    try {
      setTestingEmail(true);
      setError("");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email/test/order-confirmation`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setSuccess('Test order confirmation email sent to hello@turbo360.com.au!');
      setTimeout(() => setSuccess(""), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const testAdminNotificationEmail = async () => {
    try {
      setTestingEmail(true);
      setError("");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email/test/admin-notification`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setSuccess('Test admin notification email sent to hello@turbo360.com.au!');
      setTimeout(() => setSuccess(""), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const testShippingNotificationEmail = async () => {
    try {
      setTestingEmail(true);
      setError("");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email/test/shipping-notification`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setSuccess('Test shipping notification email sent to hello@turbo360.com.au!');
      setTimeout(() => setSuccess(""), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const testTourConfirmationEmail = async () => {
    try {
      setTestingEmail(true);
      setError("");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email/test/tour-confirmation`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send test email');
      }

      setSuccess('Test tour confirmation email sent to hello@turbo360.com.au!');
      setTimeout(() => setSuccess(""), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setTestingEmail(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    if (section) {
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleAddOverride = () => {
    if (!newOverride.start_date || !newOverride.end_date) return;
    const overrides = [...(settings.tour_schedule?.overrides || []), { ...newOverride }];
    setSettings(prev => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides }
    }));
    setNewOverride({
      label: '', start_date: '', end_date: '', apply_monday: true, apply_friday: true,
      allow_morning: false, allow_afternoon: false, enabled: true
    });
    setShowAddOverride(false);
  };

  const handleRemoveOverride = (index) => {
    const overrides = [...(settings.tour_schedule?.overrides || [])];
    overrides.splice(index, 1);
    setSettings(prev => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides }
    }));
  };

  const handleToggleOverride = (index) => {
    const overrides = [...(settings.tour_schedule?.overrides || [])];
    overrides[index] = { ...overrides[index], enabled: !overrides[index].enabled };
    setSettings(prev => ({
      ...prev,
      tour_schedule: { ...prev.tour_schedule, overrides }
    }));
  };

  const addEmailAddress = () => {
    const newEmail = prompt("Enter new admin notification email:");
    if (newEmail && newEmail.includes('@')) {
      setSettings(prev => ({
        ...prev,
        email: {
          ...prev.email,
          admin_notification_emails: [...prev.email.admin_notification_emails, newEmail]
        }
      }));
    }
  };

  const removeEmailAddress = (index) => {
    setSettings(prev => ({
      ...prev,
      email: {
        ...prev.email,
        admin_notification_emails: prev.email.admin_notification_emails.filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Settings className="w-8 h-8 mr-3 text-purple-600" />
                System Settings
              </h1>
              <p className="text-gray-600 mt-1">Configure shipping, email notifications, and shop settings</p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <Alert className="mb-6 border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-600">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-600">{success}</AlertDescription>
          </Alert>
        )}

        {/* Settings Tabs */}
        <Tabs defaultValue="shipping" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="shipping">
              <Truck className="w-4 h-4 mr-2" />
              Shipping
            </TabsTrigger>
            <TabsTrigger value="email">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="opening-hours">
              <Clock className="w-4 h-4 mr-2" />
              Hours
            </TabsTrigger>
            <TabsTrigger value="tour-pricing">
              <Users className="w-4 h-4 mr-2" />
              Tours
            </TabsTrigger>
            <TabsTrigger value="stripe">
              <CreditCard className="w-4 h-4 mr-2" />
              Stripe
            </TabsTrigger>
            <TabsTrigger value="features">
              <Settings className="w-4 h-4 mr-2" />
              Features
            </TabsTrigger>
            <TabsTrigger value="general">
              <Globe className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
          </TabsList>

          {/* Shipping Settings */}
          <TabsContent value="shipping">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Truck className="w-5 h-5 mr-2 text-purple-600" />
                  Shipping Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <DollarSign className="w-4 h-4 inline mr-1" />
                      Shipping Fee (AUD)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.shipping.shipping_fee}
                      onChange={(e) => handleInputChange('shipping', 'shipping_fee', parseFloat(e.target.value) || 0)}
                      placeholder="15.00"
                    />
                    <p className="text-sm text-gray-500 mt-1">Standard shipping fee for all Australian orders</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Free Shipping Threshold (AUD)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={settings.shipping.free_shipping_threshold || ""}
                      onChange={(e) => handleInputChange('shipping', 'free_shipping_threshold', e.target.value ? parseFloat(e.target.value) : null)}
                      placeholder="100.00 (optional)"
                    />
                    <p className="text-sm text-gray-500 mt-1">Minimum order amount for free shipping (leave empty to disable)</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Allowed Countries
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {settings.shipping.allowed_countries.map((country, index) => (
                      <Badge key={index} variant="outline" className="text-purple-600 border-purple-200">
                        {country === 'AU' ? '🇦🇺 Australia' : country}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Currently shipping to Australia only</p>
                </div>

                <div className="pt-4 border-t">
                  <Button onClick={() => updateSettings('shipping')} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Shipping Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Settings */}
          <TabsContent value="email">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="w-5 h-5 mr-2 text-purple-600" />
                  Email Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SMTP Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SMTP Host
                    </label>
                    <Input
                      value={settings.email.smtp_host}
                      onChange={(e) => handleInputChange('email', 'smtp_host', e.target.value)}
                      placeholder="smtp.postmarkapp.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SMTP Port
                    </label>
                    <Input
                      type="number"
                      value={settings.email.smtp_port}
                      onChange={(e) => handleInputChange('email', 'smtp_port', parseInt(e.target.value) || 587)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SMTP Username
                    </label>
                    <Input
                      value={settings.email.smtp_user}
                      onChange={(e) => handleInputChange('email', 'smtp_user', e.target.value)}
                      placeholder="Your Postmark API Token"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      SMTP Password
                    </label>
                    <Input
                      type="password"
                      value={settings.email.smtp_password}
                      onChange={(e) => handleInputChange('email', 'smtp_password', e.target.value)}
                      placeholder="Your Postmark API Token"
                    />
                  </div>
                </div>

                {/* Sender Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Email
                    </label>
                    <Input
                      type="email"
                      value={settings.email.sender_email}
                      onChange={(e) => handleInputChange('email', 'sender_email', e.target.value)}
                      placeholder="admin@umoonaopalmine.com.au"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sender Name
                    </label>
                    <Input
                      value={settings.email.sender_name}
                      onChange={(e) => handleInputChange('email', 'sender_name', e.target.value)}
                      placeholder="Umoona Opal Mine"
                    />
                  </div>
                </div>

                {/* Admin Notification Emails */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Admin Notification Emails
                  </label>
                  <div className="space-y-2">
                    {settings.email.admin_notification_emails.map((email, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => {
                            const newEmails = [...settings.email.admin_notification_emails];
                            newEmails[index] = e.target.value;
                            handleInputChange('email', 'admin_notification_emails', newEmails);
                          }}
                          className="flex-1"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeEmailAddress(index)}
                          disabled={settings.email.admin_notification_emails.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addEmailAddress}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Email Address
                    </Button>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Emails that will receive new order notifications</p>
                </div>

                {/* Email Test Section */}
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-4 flex items-center">
                    <TestTube2 className="w-4 h-4 mr-2" />
                    Email Template Testing
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Basic Configuration Test */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h5 className="font-medium text-gray-900 mb-2">Basic Configuration Test</h5>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          placeholder="test@example.com"
                          className="flex-1"
                        />
                        <Button onClick={testEmailSettings} disabled={testingEmail || !testEmail}>
                          {testingEmail ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Send a basic test email to verify SMTP configuration</p>
                    </div>

                    {/* Template Tests */}
                    <div className="bg-white p-4 rounded-lg border">
                      <h5 className="font-medium text-gray-900 mb-3">Template Preview Tests</h5>
                      <p className="text-xs text-gray-600 mb-3">All template tests will be sent to hello@turbo360.com.au</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button 
                          onClick={testOrderConfirmationEmail} 
                          disabled={testingEmail}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                        >
                          <Mail className="w-4 h-4 mr-2 text-green-600" />
                          1. Order Confirmation
                        </Button>
                        
                        <Button 
                          onClick={testAdminNotificationEmail}
                          disabled={testingEmail}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                        >
                          <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                          2. Admin Order Alert
                        </Button>
                        
                        <Button 
                          onClick={testShippingNotificationEmail}
                          disabled={testingEmail}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                        >
                          <Truck className="w-4 h-4 mr-2 text-blue-600" />
                          3. Shipping Notification
                        </Button>
                        
                        <Button 
                          onClick={testTourConfirmationEmail}
                          disabled={testingEmail}
                          variant="outline"
                          size="sm"
                          className="justify-start"
                        >
                          <Clock className="w-4 h-4 mr-2 text-purple-600" />
                          4. Tour Confirmation
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button onClick={() => updateSettings('email')} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Email Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Opening Hours Settings */}
          <TabsContent value="opening-hours">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-purple-600" />
                  Opening Hours Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label htmlFor="closed-tuesdays" className="font-medium text-gray-900 flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="closed-tuesdays"
                          checked={settings.opening_hours?.closed_on_tuesdays || false}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            opening_hours: {
                              ...prev.opening_hours,
                              closed_on_tuesdays: e.target.checked
                            }
                          }))}
                          className="mr-3 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          data-testid="closed-tuesdays-checkbox"
                        />
                        Closed on Tuesdays
                      </label>
                      <p className="text-sm text-gray-600 mt-1 ml-8">
                        When enabled, displays "Closed on Tuesdays" notice across the website
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label htmlFor="holiday-hours" className="font-medium text-gray-900 flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="holiday-hours"
                          checked={settings.opening_hours?.holiday_hours_notice || false}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            opening_hours: {
                              ...prev.opening_hours,
                              holiday_hours_notice: e.target.checked
                            }
                          }))}
                          className="mr-3 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                          data-testid="holiday-hours-checkbox"
                        />
                        Holiday Hours Notice
                      </label>
                      <p className="text-sm text-gray-600 mt-1 ml-8">
                        Displays: "Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026."
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Preview
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="font-medium text-gray-900">8:30am - 5:30pm</div>
                      {settings.opening_hours?.closed_on_tuesdays && (
                        <div className="text-red-600">Closed on Tuesdays</div>
                      )}
                      {settings.opening_hours?.holiday_hours_notice && (
                        <div className="text-amber-600">
                          <div className="font-semibold">Seasonal Closure</div>
                          <div>Umoona Opal Mine is seasonally closed for entire month of February. We will re-open to the public on 2 March 2026.</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => updateSettings('opening_hours')}
                    disabled={saving}
                    data-testid="save-opening-hours-btn"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Opening Hours Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tour Pricing Settings */}
          <TabsContent value="tour-pricing">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2 text-purple-600" />
                  Tour Pricing Management
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Configure pricing for underground mine tour tickets and packages.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Base Pricing */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 border-2 border-purple-200">
                  <h3 className="text-lg font-semibold text-purple-800 mb-4">Individual Ticket Pricing</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adult Price (AUD)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={settings.tour_pricing?.adult_price || 15.50}
                          onChange={(e) => handleInputChange('tour_pricing', 'adult_price', parseFloat(e.target.value))}
                          placeholder="15.50"
                          className="pl-7"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Child Price (Under 16)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={settings.tour_pricing?.child_price || 8.50}
                          onChange={(e) => handleInputChange('tour_pricing', 'child_price', parseFloat(e.target.value))}
                          placeholder="8.50"
                          className="pl-7"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Family Package Pricing with Add/Remove Options */}
                <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6 border-2 border-green-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-green-800">Family Package Pricing</h3>
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddFamilyPackage(true)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Family Package
                    </Button>
                  </div>
                  
                  {/* Existing Family Packages */}
                  <div className="space-y-3">
                    {(settings.tour_pricing?.family_packages || []).map((pkg, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={pkg.enabled !== false}
                            onChange={(e) => {
                              const updatedPackages = [...(settings.tour_pricing?.family_packages || [])];
                              updatedPackages[index].enabled = e.target.checked;
                              handleInputChange('tour_pricing', 'family_packages', updatedPackages);
                            }}
                            className="h-4 w-4"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{pkg.name}</p>
                            <p className="text-sm text-gray-600">${pkg.price} - {pkg.description}</p>
                            <p className="text-xs text-green-600 font-semibold mt-1">👥 {pkg.guest_count || 0} guests</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            const updatedPackages = (settings.tour_pricing?.family_packages || []).filter((_, i) => i !== index);
                            handleInputChange('tour_pricing', 'family_packages', updatedPackages);
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* Show default message if no packages */}
                    {(!settings.tour_pricing?.family_packages || settings.tour_pricing.family_packages.length === 0) && (
                      <div className="text-center py-6 text-gray-500">
                        <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                        <p>No family packages configured. Click "Add Family Package" to create one.</p>
                      </div>
                    )}
                  </div>

                  {/* Add Family Package Form */}
                  {showAddFamilyPackage && (
                    <div className="mt-4 p-4 bg-white rounded-lg border-2 border-green-300">
                      <h4 className="font-semibold text-gray-900 mb-3">Add New Family Package</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <Input
                          placeholder="Package Name (e.g., Family of 4)"
                          value={newFamilyPackage.name}
                          onChange={(e) => setNewFamilyPackage(prev => ({...prev, name: e.target.value}))}
                        />
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={newFamilyPackage.price}
                            onChange={(e) => setNewFamilyPackage(prev => ({...prev, price: parseFloat(e.target.value) || 0}))}
                            className="pl-7"
                          />
                        </div>
                        <Input
                          placeholder="Description (e.g., 2 Adults + 2 Children under 16)"
                          value={newFamilyPackage.description}
                          onChange={(e) => setNewFamilyPackage(prev => ({...prev, description: e.target.value}))}
                          className="md:col-span-1"
                        />
                        <Input
                          type="number"
                          placeholder="Guest Count (e.g., 4)"
                          value={newFamilyPackage.guest_count || ''}
                          onChange={(e) => setNewFamilyPackage(prev => ({...prev, guest_count: parseInt(e.target.value) || 0}))}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm"
                          onClick={() => {
                            if (newFamilyPackage.name && newFamilyPackage.price && newFamilyPackage.description && newFamilyPackage.guest_count) {
                              const updatedPackages = [...(settings.tour_pricing?.family_packages || []), newFamilyPackage];
                              handleInputChange('tour_pricing', 'family_packages', updatedPackages);
                              setNewFamilyPackage({ name: '', price: '', description: '', guest_count: '', enabled: true });
                              setShowAddFamilyPackage(false);
                            }
                          }}
                          disabled={!newFamilyPackage.name || !newFamilyPackage.price || !newFamilyPackage.description || !newFamilyPackage.guest_count}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Add Package
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowAddFamilyPackage(false);
                            setNewFamilyPackage({ name: '', price: '', description: '', guest_count: '', enabled: true });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Packages Management */}
                <div className="bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg p-6 border-2 border-rose-200">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-rose-800">Custom Tour Packages</h3>
                    <Button 
                      size="sm" 
                      onClick={() => setShowAddPackage(true)}
                      className="bg-rose-600 hover:bg-rose-700"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Package
                    </Button>
                  </div>
                  
                  {/* Existing Custom Packages */}
                  <div className="space-y-3">
                    {(settings.tour_pricing?.custom_packages || []).map((pkg, index) => (
                      <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border">
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={pkg.enabled !== false}
                            onChange={(e) => {
                              const updatedPackages = [...(settings.tour_pricing?.custom_packages || [])];
                              updatedPackages[index].enabled = e.target.checked;
                              handleInputChange('tour_pricing', 'custom_packages', updatedPackages);
                            }}
                            className="h-4 w-4"
                          />
                          <div>
                            <p className="font-medium text-gray-900">{pkg.name}</p>
                            <p className="text-sm text-gray-600">${pkg.price} - {pkg.description}</p>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            const updatedPackages = (settings.tour_pricing?.custom_packages || []).filter((_, i) => i !== index);
                            handleInputChange('tour_pricing', 'custom_packages', updatedPackages);
                          }}
                          className="text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Add Package Form */}
                  {showAddPackage && (
                    <div className="mt-4 p-4 bg-white rounded-lg border-2 border-rose-300">
                      <h4 className="font-semibold text-gray-900 mb-3">Add New Package</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <Input
                          placeholder="Package Name (e.g., Senior)"
                          value={newPackage.name}
                          onChange={(e) => setNewPackage(prev => ({...prev, name: e.target.value}))}
                        />
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="Price"
                            value={newPackage.price}
                            onChange={(e) => setNewPackage(prev => ({...prev, price: parseFloat(e.target.value) || 0}))}
                            className="pl-7"
                          />
                        </div>
                        <Input
                          placeholder="Description"
                          value={newPackage.description}
                          onChange={(e) => setNewPackage(prev => ({...prev, description: e.target.value}))}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm"
                          onClick={() => {
                            if (newPackage.name && newPackage.price && newPackage.description) {
                              const updatedPackages = [...(settings.tour_pricing?.custom_packages || []), newPackage];
                              handleInputChange('tour_pricing', 'custom_packages', updatedPackages);
                              setNewPackage({ name: '', price: '', description: '', enabled: true });
                              setShowAddPackage(false);
                            }
                          }}
                          disabled={!newPackage.name || !newPackage.price || !newPackage.description}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Add Package
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setShowAddPackage(false);
                            setNewPackage({ name: '', price: '', description: '', enabled: true });
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Tour Schedule Settings */}
                <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-6 border-2 border-orange-200">
                  <h3 className="text-lg font-semibold text-orange-800 mb-4 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    Tour Schedule Configuration
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Control tour time availability across different days of the week.
                  </p>

                  <div className="bg-white rounded-lg p-4 border border-orange-300 space-y-4">
                    <div className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        id="restrict-monday-friday"
                        checked={settings.tour_schedule?.restrict_monday_friday !== false}
                        onChange={(e) => {
                          handleInputChange('tour_schedule', 'restrict_monday_friday', e.target.checked);
                        }}
                        className="mt-1 h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <div className="flex-1">
                        <label htmlFor="restrict-monday-friday" className="text-sm font-medium text-gray-900 cursor-pointer">
                          Restrict Monday & Friday Tours
                        </label>
                        <p className="text-sm text-gray-600 mt-1">
                          When <strong>enabled (checked)</strong>: Mondays & Fridays only have 4:00 PM tour available. Other days have 10:00 AM, 2:00 PM, and 4:00 PM tours.
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          When <strong>disabled (unchecked)</strong>: All days (including Mondays & Fridays) have 10:00 AM, 2:00 PM, and 4:00 PM tours available.
                        </p>
                        {settings.tour_schedule?.restrict_monday_friday === false && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                            <p className="text-sm text-green-800 font-medium">
                              ✓ All tour times enabled for every day of the week
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Date Range for Restriction */}
                    {settings.tour_schedule?.restrict_monday_friday !== false && (<>
                      <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <h4 className="text-sm font-semibold text-orange-800 mb-3">Restriction Date Range</h4>
                        <p className="text-xs text-gray-600 mb-3">
                          Set when the Monday & Friday restriction applies. Leave empty for the restriction to apply immediately and indefinitely.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Start Date
                            </label>
                            <input
                              type="date"
                              value={settings.tour_schedule?.restriction_start_date || ''}
                              onChange={(e) => {
                                handleInputChange('tour_schedule', 'restriction_start_date', e.target.value || null);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Restriction starts from this date</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              End Date (optional)
                            </label>
                            <input
                              type="date"
                              value={settings.tour_schedule?.restriction_end_date || ''}
                              onChange={(e) => {
                                handleInputChange('tour_schedule', 'restriction_end_date', e.target.value || null);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Leave empty to apply indefinitely</p>
                          </div>
                        </div>
                        {settings.tour_schedule?.restriction_start_date && (
                          <div className="mt-3 p-2 bg-amber-100 border border-amber-300 rounded">
                            <p className="text-sm text-amber-800">
                              <strong>Active Period:</strong> Restriction applies from{' '}
                              <strong>{new Date(settings.tour_schedule.restriction_start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                              {settings.tour_schedule?.restriction_end_date ? (
                                <> until <strong>{new Date(settings.tour_schedule.restriction_end_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong></>
                              ) : (
                                <> onwards (indefinitely)</>
                              )}
                            </p>
                          </div>
                        )}
                        {!settings.tour_schedule?.restriction_start_date && (
                          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                            <p className="text-sm text-blue-800">
                              <strong>Note:</strong> No start date set. Restriction is active immediately for all dates.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Override Conditions */}
                      <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                        <h4 className="text-sm font-semibold text-green-800 mb-2">Override Conditions</h4>
                        <p className="text-xs text-gray-600 mb-3">
                          Temporarily re-open additional tour times on restricted Mondays/Fridays during specific date ranges (e.g. school holidays, festivals).
                        </p>

                        {/* List existing overrides */}
                        {(settings.tour_schedule?.overrides || []).length > 0 && (
                          <div className="space-y-2 mb-3">
                            {(settings.tour_schedule?.overrides || []).map((override, index) => (
                              <div key={index} className={`p-3 rounded border ${override.enabled ? 'bg-white border-green-300' : 'bg-gray-50 border-gray-300 opacity-60'}`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {override.label || `Override #${index + 1}`}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-2">
                                      {override.start_date && new Date(override.start_date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
                                      {' - '}
                                      {override.end_date && new Date(override.end_date + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                    <div className="text-xs text-gray-600 mt-1">
                                      Days: {[override.apply_monday && 'Mon', override.apply_friday && 'Fri'].filter(Boolean).join(', ') || 'None'}
                                      {' | '}
                                      Allow: {[override.allow_morning && '10am', override.allow_afternoon && '2pm'].filter(Boolean).join(', ') || 'None'}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2 ml-3">
                                    <button
                                      type="button"
                                      onClick={() => handleToggleOverride(index)}
                                      className={`px-2 py-1 text-xs rounded ${override.enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                    >
                                      {override.enabled ? 'Enabled' : 'Disabled'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveOverride(index)}
                                      className="p-1 text-red-400 hover:text-red-600"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Add Override Form */}
                        {showAddOverride ? (
                          <div className="p-3 bg-white rounded border border-green-300 space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Label (optional)</label>
                              <input
                                type="text"
                                placeholder="e.g. School Holidays April"
                                value={newOverride.label}
                                onChange={(e) => setNewOverride(prev => ({ ...prev, label: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Start Date *</label>
                                <input
                                  type="date"
                                  value={newOverride.start_date}
                                  onChange={(e) => setNewOverride(prev => ({ ...prev, start_date: e.target.value }))}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">End Date *</label>
                                <input
                                  type="date"
                                  value={newOverride.end_date}
                                  onChange={(e) => setNewOverride(prev => ({ ...prev, end_date: e.target.value }))}
                                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Apply to days:</label>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-1 text-sm">
                                  <input type="checkbox" checked={newOverride.apply_monday} onChange={(e) => setNewOverride(prev => ({ ...prev, apply_monday: e.target.checked }))} className="h-4 w-4 text-green-600 rounded" />
                                  Mondays
                                </label>
                                <label className="flex items-center gap-1 text-sm">
                                  <input type="checkbox" checked={newOverride.apply_friday} onChange={(e) => setNewOverride(prev => ({ ...prev, apply_friday: e.target.checked }))} className="h-4 w-4 text-green-600 rounded" />
                                  Fridays
                                </label>
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">Re-open tour times:</label>
                              <div className="flex gap-4">
                                <label className="flex items-center gap-1 text-sm">
                                  <input type="checkbox" checked={newOverride.allow_morning} onChange={(e) => setNewOverride(prev => ({ ...prev, allow_morning: e.target.checked }))} className="h-4 w-4 text-green-600 rounded" />
                                  10:00 AM
                                </label>
                                <label className="flex items-center gap-1 text-sm">
                                  <input type="checkbox" checked={newOverride.allow_afternoon} onChange={(e) => setNewOverride(prev => ({ ...prev, allow_afternoon: e.target.checked }))} className="h-4 w-4 text-green-600 rounded" />
                                  2:00 PM
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">4:00 PM is always available on restricted days</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={handleAddOverride}
                                disabled={!newOverride.start_date || !newOverride.end_date}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Plus className="w-4 h-4 inline mr-1" />
                                Add Override
                              </button>
                              <button
                                type="button"
                                onClick={() => setShowAddOverride(false)}
                                className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowAddOverride(true)}
                            className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 border border-green-300"
                          >
                            <Plus className="w-4 h-4 inline mr-1" />
                            Add Override Condition
                          </button>
                        )}
                      </div>
                    </>)}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button onClick={() => updateSettings('tour_pricing')} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Tour Pricing
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stripe Settings */}
          <TabsContent value="stripe">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CreditCard className="w-5 h-5 mr-2 text-blue-600" />
                  Stripe Payment Configuration
                </CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  Configure your Stripe API keys for processing payments. Supports both Test (Sandbox) and Live (Production) modes.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Mode Toggle */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border-2 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <label htmlFor="stripe-live-mode" className="font-bold text-gray-900 flex items-center cursor-pointer text-lg">
                        <input
                          type="checkbox"
                          id="stripe-live-mode"
                          checked={settings.stripe?.use_live_keys || false}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            stripe: {
                              ...prev.stripe,
                              use_live_keys: e.target.checked
                            }
                          }))}
                          className="mr-3 h-6 w-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        {settings.stripe?.use_live_keys ? (
                          <span className="text-green-700">🟢 LIVE MODE (Production)</span>
                        ) : (
                          <span className="text-orange-700">🟡 TEST MODE (Sandbox)</span>
                        )}
                      </label>
                      <p className="text-sm text-gray-600 mt-2 ml-9">
                        {settings.stripe?.use_live_keys 
                          ? "Using LIVE Stripe keys for real transactions. Customers will be charged!"
                          : "Using TEST Stripe keys for testing. No real charges will be made."
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Test/Sandbox Keys */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">TEST</span>
                    Sandbox Keys (For Testing)
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test API Key (Secret Key)
                    </label>
                    <input
                      type="password"
                      placeholder="sk_test_..."
                      value={settings.stripe?.test_api_key || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, test_api_key: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Server-side API key (starts with sk_test_)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Publishable Key
                    </label>
                    <input
                      type="text"
                      placeholder="pk_test_..."
                      value={settings.stripe?.test_publishable_key || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, test_publishable_key: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Client-side key (starts with pk_test_)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Test Webhook Secret
                    </label>
                    <input
                      type="password"
                      placeholder="whsec_..."
                      value={settings.stripe?.test_webhook_secret || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, test_webhook_secret: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Webhook secret (starts with whsec_)</p>
                  </div>
                </div>

                {/* Live/Production Keys */}
                <div className="space-y-4 pt-6 border-t-2 border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span className="bg-green-600 text-white px-3 py-1 rounded-full text-sm">LIVE</span>
                    Production Keys
                  </h3>
                  
                  <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-red-800 font-semibold">
                      ⚠️ WARNING: Live keys process real transactions!
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Live API Key
                    </label>
                    <input
                      type="password"
                      placeholder="sk_live_..."
                      value={settings.stripe?.live_api_key || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, live_api_key: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Live Publishable Key
                    </label>
                    <input
                      type="text"
                      placeholder="pk_live_..."
                      value={settings.stripe?.live_publishable_key || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, live_publishable_key: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Live Webhook Secret
                    </label>
                    <input
                      type="password"
                      placeholder="whsec_..."
                      value={settings.stripe?.live_webhook_secret || ""}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        stripe: {...prev.stripe, live_webhook_secret: e.target.value}
                      }))}
                      className="w-full p-3 border border-gray-300 rounded-md font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <Button
                    onClick={() => updateSettings('stripe')}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Stripe Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Feature Settings */}
          <TabsContent value="features">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-purple-600" />
                  Feature Toggles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <label htmlFor="tour-booking-enabled" className="font-medium text-gray-900 flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="tour-booking-enabled"
                          checked={settings.features?.tour_booking_enabled !== false}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            features: {
                              ...prev.features,
                              tour_booking_enabled: e.target.checked
                            }
                          }))}
                          className="mr-3 h-5 w-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        Enable Tour Booking
                      </label>
                      <p className="text-sm text-gray-600 mt-1 ml-8">
                        When disabled, the Tour Tickets button will be hidden from the menu, booking buttons removed from Underground Tours page, and the Book Tours page will be inaccessible.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex-1">
                      <label htmlFor="store-maintenance-mode" className="font-medium text-gray-900 flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          id="store-maintenance-mode"
                          checked={settings.features?.store_maintenance_mode === true}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            features: {
                              ...prev.features,
                              store_maintenance_mode: e.target.checked
                            }
                          }))}
                          className="mr-3 h-5 w-5 text-red-600 rounded focus:ring-2 focus:ring-red-500"
                        />
                        🚧 Store Maintenance Mode
                      </label>
                      <p className="text-sm text-gray-600 mt-1 ml-8">
                        When enabled, the online store will display a maintenance message instead of products. Existing orders will still be accessible to customers.
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Important
                    </h4>
                    <p className="text-sm text-amber-800">
                      Disabling tour booking will immediately hide all tour booking functionality from customers. Existing bookings will not be affected.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => updateSettings('features')}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Feature Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* General Settings */}
          <TabsContent value="general">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Globe className="w-5 h-5 mr-2 text-purple-600" />
                  General Shop Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shop Name
                    </label>
                    <Input
                      value={settings.shop_name}
                      onChange={(e) => handleInputChange(null, 'shop_name', e.target.value)}
                      placeholder="Umoona Opal Mine"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shop URL
                    </label>
                    <Input
                      value={settings.shop_url}
                      onChange={(e) => handleInputChange(null, 'shop_url', e.target.value)}
                      placeholder="https://umoonaopalmine.com.au"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency
                    </label>
                    <Input
                      value={settings.currency}
                      onChange={(e) => handleInputChange(null, 'currency', e.target.value)}
                      placeholder="AUD"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency Symbol
                    </label>
                    <Input
                      value={settings.currency_symbol}
                      onChange={(e) => handleInputChange(null, 'currency_symbol', e.target.value)}
                      placeholder="$"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax Rate (GST)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="1"
                      value={settings.tax_rate}
                      onChange={(e) => handleInputChange(null, 'tax_rate', parseFloat(e.target.value) || 0)}
                      placeholder="0.10"
                    />
                    <p className="text-sm text-gray-500 mt-1">Enter as decimal (0.10 = 10%)</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="taxInclusive"
                      checked={settings.tax_inclusive}
                      onChange={(e) => handleInputChange(null, 'tax_inclusive', e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="taxInclusive" className="text-sm font-medium text-gray-700">
                      Prices include tax (tax inclusive)
                    </label>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button onClick={() => updateSettings()} disabled={saving}>
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save General Settings
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminSettingsPage;